'use client';

import { useState, useEffect } from 'react';
import { isIOS } from '@/lib/platform';

export interface FeatureFlags {
  signUpAllowed: boolean;
  paymentAllowed: boolean;
  premiumFeaturesAllowed: boolean;
}

// Default flags (all disabled) used during loading and on error
const DEFAULT_FLAGS: FeatureFlags = {
  signUpAllowed: false,
  paymentAllowed: false,
  premiumFeaturesAllowed: false,
};

// Cache flags for 1 minute
let cachedFlags: FeatureFlags | null = null;
let cacheTime: number = 0;
const CACHE_DURATION = 60 * 1000; // 1 minute

/**
 * Hook to fetch and use feature flags from the API
 *
 * Feature flags control the availability of features like signup,
 * payment, and premium content without requiring code changes.
 *
 * @returns Feature flags object with loading state
 *
 * @example
 * ```tsx
 * const { flags, loading } = useFeatureFlags();
 *
 * if (!flags.signUpAllowed) {
 *   return <ComingSoonModal type="signup" />;
 * }
 * ```
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags || DEFAULT_FLAGS);
  const [loading, setLoading] = useState(!cachedFlags);

  useEffect(() => {
    // Use cached flags if still fresh
    const now = Date.now();
    if (cachedFlags && now - cacheTime < CACHE_DURATION) {
      setFlags(cachedFlags);
      setLoading(false);
      return;
    }

    // Fetch fresh flags
    async function fetchFlags() {
      try {
        const response = await fetch('/api/public/features');

        if (!response.ok) {
          console.warn('Failed to fetch feature flags, using defaults');
          setFlags(DEFAULT_FLAGS);
          setLoading(false);
          return;
        }

        const data = await response.json();

        // Override: hide payment UI on iOS native (Apple Guideline 3.1.1)
        if (isIOS()) {
          data.paymentAllowed = false;
        }

        // Update cache
        cachedFlags = data;
        cacheTime = Date.now();

        setFlags(data);
      } catch (error) {
        console.error('Error fetching feature flags:', error);
        setFlags(DEFAULT_FLAGS);
      } finally {
        setLoading(false);
      }
    }

    fetchFlags();
  }, []);

  return { flags, loading };
}

/**
 * Clear the feature flags cache
 * Useful for testing or when flags change
 */
export function clearFeatureFlagsCache() {
  cachedFlags = null;
  cacheTime = 0;
}
